"use client"

import { CardContent } from "@/components/card"
import { Card } from "@/components/card"
import { Link, useSearchParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/forms/form/Form"
import FormWrap from "@/components/forms/form/FormWrap"
import { Input } from "@/components/forms/inputs/text/Input"
import { useState } from "react"
import { Alert } from "@/components/alert"
import { resetPasswordService } from "@/api/services/auth/authService"
import displayError from "@/api/errors/errorResponse"

const recoverySchema = z.object({
  password: z.string()
    .min(12, "Hasło musi mieć minimum 12 znaków")
    .max(48, "Hasło nie może przekraczać 48 znaków")
    .regex(/[A-Z]/, "Hasło musi zawierać przynajmniej jedną dużą literę")
    .regex(/[a-z]/, "Hasło musi zawierać przynajmniej jedną małą literę")
    .regex(/[0-9]/, "Hasło musi zawierać przynajmniej jedną cyfrę")
    .regex(/[^A-Za-z0-9]/, "Hasło musi zawierać przynajmniej jeden znak specjalny"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Hasła nie są identyczne",
  path: ["confirmPassword"]
})

type RecoveryData = z.infer<typeof recoverySchema>

export default function Recovery() {
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const userId = searchParams.get('id')

  const form = useForm<RecoveryData>({
    resolver: zodResolver(recoverySchema),
    defaultValues: {
      password: "",
      confirmPassword: ""
    }
  })

  const onSubmit = async (data: RecoveryData) => {
    setLoading(true)
    try {
      if (!token || !userId) {
        setError("Nieprawidłowy link do resetowania hasła")
        setLoading(false)
        return
      }
      
      const res = await resetPasswordService({ userId, token, password: data.password })
      if (!res.success) {
        displayError(res)
        setError("Wystąpił błąd podczas resetowania hasła")
        setSuccess(null)
        setLoading(false)
        return
      }
      
      setSuccess("Hasło zostało pomyślnie zmienione")
      setError(null)
    } catch (err) {
      setError("Wystąpił błąd podczas resetowania hasła")
      setSuccess(null)
    }
    setLoading(false)
  }

  if (!token || !userId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardContent className="p-6">
            <Alert variant="error" className="mb-4">
              Nieprawidłowy link do resetowania hasła
            </Alert>
            <div className="mt-6 text-center">
              <Link to="/forgotpassword" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                Poproś o nowy link do resetowania hasła
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <div className="bg-gradient-to-r from-purple-400 to-purple-500 p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white text-xl font-semibold mb-1">Resetowanie hasła</h1>
              <p className="text-purple-100 text-sm">Wprowadź nowe hasło do swojego konta.</p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-20 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                <div className="w-12 h-10 bg-white/30 rounded"></div>
              </div>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-4">
              {success}
            </Alert>
          )}

          {!success && (
            <Form {...form}>
              <FormWrap
                buttonClassName="w-full bg-purple-600 hover:bg-purple-700 mt-6"
                onSubmit={form.handleSubmit(onSubmit)}
                submitText="Zresetuj hasło"
                disabled={loading}
              >
                <div className="flex w-full flex-col gap-5">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nowe hasło</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Wprowadź nowe hasło"
                            type="password"
                            {...field}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Potwierdź nowe hasło</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Potwierdź nowe hasło"
                            type="password"
                            {...field}
                            required
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormWrap>
            </Form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-purple-600 hover:text-purple-700 font-medium">
              Powróć do logowania
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
